import { ReactNode } from 'react'
import {
    ButtonWrapper,
    IntegrationTitle,
    TopSectionWrapper,
    DescriptionWrapper,
    IntegrationWrapper,
} from './IntegrationProvider.styles'
import Typography from '@/ui/components/typography'
import { useCustomTheme } from '@/core/theme'
import { Divider } from '@/ui/components/Divider/Divider.styles'
import { IntegrationsTable } from '../integration/IntegrationsTable'
import { TIntegrationProvider } from '@/core/store/Integrations/integrations.store'
import { usePendingProviders } from '@/core/hooks/usePendingProviders/usePendingProviders'
import InfoBanner from '@/ui/components/BannerAlert/InfoBanner'
import { ActionButton } from '@/ui/components/buttons/action'
import { useIntegrationProvider } from './hooks/useIntegrationProvider'

export type IntegrationProps = {
    dataTestId?: string
    icon: ReactNode
    description: string
    provider: TIntegrationProvider
    name: string
}

const IntegrationProvider = ({
    dataTestId,
    provider,
    icon,
    description,
    name,
}: IntegrationProps) => {
    const theme: any = useCustomTheme()
    const { isCurrentProviderLoading } = usePendingProviders(provider)
    const {
        shouldShowAddAccount,
        shouldShowUpdateAccountBanner,
        handleAddAccount,
        activeAndBrokenIntegrations,
        isActionButtonLoading,
    } = useIntegrationProvider(provider)

    return (
        <IntegrationWrapper data-testid={dataTestId}>
            <TopSectionWrapper>
                <IntegrationTitle data-testid={`provider-card-${name}-title`}>
                    {icon}
                </IntegrationTitle>
            </TopSectionWrapper>
            <DescriptionWrapper
                data-testid={`provider-card-${name}-description`}
            >
                <Typography variant="s-regular" color={theme.colors.greys[700]}>
                    {description}
                </Typography>
            </DescriptionWrapper>
            <Divider
                color={theme.colors.semantic.integrationProviderCardDivider}
                margin="0 0 12px 0"
            />
            <IntegrationsTable
                name={name}
                integrations={activeAndBrokenIntegrations}
                type={provider.type}
                dataTestId={`provider-card-${name}-integrations-table`}
            />
            {shouldShowAddAccount && (
                <ButtonWrapper>
                    <ActionButton
                        data-testid={`provider-card-${name}-add-account-button`}
                        text={
                            isCurrentProviderLoading
                                ? 'Connecting to account'
                                : 'Add Account'
                        }
                        onClick={handleAddAccount}
                        loading={isCurrentProviderLoading}
                        disabled={isCurrentProviderLoading}
                    />
                </ButtonWrapper>
            )}
            {shouldShowUpdateAccountBanner && (
                <InfoBanner
                    dataTestId={`provider-card-${name}-update-account-banner`}
                    variant="warning"
                    title="Update is Required"
                    description="Account is already connected and being monitored for security posture issues.
To access additional security services, including Fraud Defense, a quick and easy integration update is required."
                    primaryButton={
                        <ActionButton
                            data-testid={`provider-card-${name}-update-account-button`}
                            text={
                                isActionButtonLoading
                                    ? 'Connecting to account'
                                    : 'Update'
                            }
                            onClick={handleAddAccount}
                            loading={isActionButtonLoading}
                            disabled={isActionButtonLoading}
                        />
                    }
                />
            )}
        </IntegrationWrapper>
    )
}

export default IntegrationProvider
